/**
 * Phase 1 spine — the per-program 4-KPI spine (the flagged "no per-program KPI
 * spine" gap). Renders the SDS scorecard at the top of a program as one
 * SpecraMetricStrip:
 *
 *   ① On-time delivery   ② Customer (CPARS/QASP)   ③ Financial (funding consumed)   ④ SDS audit
 *
 * Projection-aware: it composes useView for the no_view gate, and the two
 * sensitive KPI values (customer scores, financial funding consumption) route
 * through <MaskedField> — so a Masked/Simplified viewer still sees each KPI's
 * status (a Summary signal) but the underlying figures read "Restricted",
 * never leaked. On-time and audit-pass are not financial and show to everyone.
 *
 * On-time comes from the contract model (D104, D112 #6): pangaea's `on_time`
 * counts over the program's KPI window, the contract period containing today
 * plus open dates carried over — the same number the Deliverables page shows.
 *
 * Each tile links to the section that feeds it.
 */ import { jsx as _jsx } from "react/jsx-runtime";
import { Skeleton, SpecraInlineBanner, SpecraMetricStrip } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { MaskedField } from '../../../lib/access/MaskedField';
import { financialSectionVisible, programRef } from '../../../lib/access/types';
import { useView } from '../../../lib/access/useView';
import { renderRouterLink } from '../../../lib/renderRouterLink';
import { OccurrenceWindow, useOccurrences } from '../../../pages/Delivery/Deliverables/hooks';
import { onTimePercent } from '../../../pages/Delivery/Deliverables/lib/status';
import { useProgramFinancialSummary } from './useProgramFinancialSummary';
// Default funding-alert threshold when a program hasn't overridden it —
// mirrors ryugu's defaultFundingAlertThresholdPct (financial_health.go).
const DEFAULT_FUNDING_ALERT_THRESHOLD_PCT = 75;
// status: no data → neutral nodata (checked FIRST — a compliant flag computed
// over zero data must not read green); compliant → green; otherwise → red.
function kpiStatus(compliant, hasData) {
    if (!hasData) return 'nodata';
    return compliant ? 'green' : 'red';
}
const STATUS_TEXT = {
    green: 'On target',
    red: 'Off target'
};
const STATUS_TONE = {
    green: 'success',
    red: 'danger',
    nodata: 'neutral'
};
// The section that feeds each KPI; its tile links there.
const SECTION = {
    onTime: 'deliverables',
    customer: 'quality',
    financial: 'financial',
    compliance: 'audits'
};
// Labels only: the strip holds its height while the figures load.
const LOADING_METRICS = [
    'On-Time',
    'Satisfaction',
    'Financial',
    'SDS Compliance'
].map((label)=>({
        id: label,
        label,
        value: null
    }));
export function KpiSpine({ programId }) {
    const ref = programRef(programId);
    const view = useView(ref);
    const { data, isLoading, isError } = useProgramFinancialSummary(programId);
    // The program's KPI window; page_size 1 asks for the counts, not the rows.
    // Its own loading state lives in the On-Time tile, never the whole spine.
    const occ = useOccurrences(programId, {
        window: OccurrenceWindow.CONTRACT_CURRENT_PERIOD,
        pageSize: 1
    });
    if (view.viewMode === 'no_view') return null;
    if (isError) {
        // Distinct from "no data yet" — a failed RPC must not render as compliant zeros.
        return /*#__PURE__*/ _jsx(SpecraInlineBanner, {
            "data-testid": Testids.deliveryKpi.spine,
            tone: "danger",
            variant: "framed",
            children: "Couldn't load program health."
        });
    }
    if (isLoading) {
        return /*#__PURE__*/ _jsx(SpecraMetricStrip, {
            "data-testid": Testids.deliveryKpi.spine,
            loading: true,
            metrics: LOADING_METRICS
        });
    }
    const kpi = data?.kpiSnapshot;
    const health = data?.financialHealth;
    // Purposeful empty state — a bare "—" reads as broken; this reads as "not
    // measured yet" (the value only lands once the program has a KPI snapshot).
    const noData = 'No data yet';
    // Financial is nav-hidden for a viewer who can see neither figure it
    // exposes; the tile must not offer a door the product deliberately closed.
    const financialLinkable = financialSectionVisible(view.fieldMasked);
    const thresholdPct = health?.thresholdPct ?? DEFAULT_FUNDING_ALERT_THRESHOLD_PCT;
    // Sensitive values route through <MaskedField>; non-sensitive render raw.
    const masked = (fieldName, node)=>/*#__PURE__*/ _jsx(MaskedField, {
            fieldName: fieldName,
            resource: ref,
            children: node
        });
    // Financial Health is computed independently of the KPI snapshot (it comes
    // from recorded financial months + funding, not the kpi_snapshot rollup),
    // so this tile doesn't depend on `kpi` being present and is shared by both
    // branches below.
    const financialHasData = !!health?.hasFunding && !!health?.hasCurrent;
    // Masked ⇒ neutral, like no-data: a masked viewer's funding_consumed_pct
    // and threshold_breached arrive zeroed (false), so deriving a status from
    // them would paint a false GREEN "on target" for a viewer who isn't
    // allowed the underlying value. Gate on the tokens this tile actually
    // consumes for its value/classification — not `financialLinkable`, which
    // only asks "can the viewer reach ANY financial figure" and stays true for
    // an exec_viewer who sees funding but not burn; that's a different question
    // from "can they see THIS tile's own numbers".
    const financialMasked = view.fieldMasked('funding_consumed_pct') || view.fieldMasked('threshold_breached');
    const financialTile = {
        label: 'Financial',
        value: financialHasData ? masked('funding_consumed_pct', `${(health?.fundingConsumedPct ?? 0).toFixed(0)}%`) : noData,
        status: financialMasked ? 'nodata' : kpiStatus(!health?.thresholdBreached, financialHasData),
        note: `Alert at ${thresholdPct}%`,
        section: SECTION.financial,
        testid: Testids.deliveryKpi.tileFinancial
    };
    // On-Time Delivery, from the contract model: independent of the snapshot.
    // No approvals and nothing late is not 0%: it states what is missing.
    const onTimeCounts = occ.data?.onTime;
    const onTimePct = onTimePercent(onTimeCounts);
    const lateCount = occ.data?.stateCounts.late ?? 0;
    const datesInWindow = Object.values(occ.data?.stateCounts ?? {}).reduce((a, n)=>a + n, 0);
    const onTimeHasData = !occ.isError && (onTimePct !== null || lateCount > 0);
    const onTimeTile = {
        label: 'On-Time',
        value: occ.isLoading ? /*#__PURE__*/ _jsx(Skeleton, {
            className: "h-6 w-14"
        }) : occ.isError ? "Couldn't load" : onTimePct !== null ? `${onTimePct}%` : lateCount > 0 ? `${lateCount} late` : datesInWindow === 0 ? 'No dates yet' : 'None approved yet',
        status: kpiStatus(onTimePct === 100 && lateCount === 0, onTimeHasData),
        note: 'Target 100%',
        section: SECTION.onTime,
        testid: Testids.deliveryKpi.tileOnTime
    };
    // Presence comes from the backend's has_* flags, never value shapes —
    // a zero-valued real entry (0-score CPARS) is data.
    const customerHasData = !!kpi && (kpi.hasCpars || kpi.hasQasp);
    // has_audit means an audit row exists; with zero scored points there is
    // nothing to pass or fail yet.
    const auditHasData = !!kpi && kpi.hasAudit && kpi.latestAuditTotal > 0;
    // Masked compliance booleans arrive zeroed (false) — deriving a status
    // from them would paint a false "Off target" red for a viewer who isn't
    // allowed the underlying value. Masked ⇒ neutral, like no-data. Gate on
    // the booleans actually consumed (plus the score value) so a PARTIAL
    // carve-out — score visible, classification masked — can't repaint the
    // fabricated red this exists to remove.
    const customerMasked = view.fieldMasked('cpars_score') || view.fieldMasked('kpi2_cpars_compliant') || view.fieldMasked('kpi2_qasp_compliant');
    const tiles = [
        onTimeTile,
        {
            label: 'Satisfaction',
            // Absent legs render as a dash, never a fabricated zero — the backend
            // zero-fills absent sources, so only has_* can tell "no QASP" from
            // "QASP scored zero".
            value: kpi && customerHasData ? masked('cpars_score', `${kpi.hasCpars ? kpi.latestCparsScore.toFixed(1) : '—'} / ${kpi.hasQasp ? kpi.latestQaspScore.toFixed(1) : '—'}`) : noData,
            status: kpi && !customerMasked ? kpiStatus(kpi.kpi2CparsCompliant && kpi.kpi2QaspCompliant, customerHasData) : 'nodata',
            note: 'Target ≥ 4.0',
            section: SECTION.customer,
            testid: Testids.deliveryKpi.tileCustomer
        },
        financialTile,
        {
            label: 'SDS Compliance',
            value: kpi && auditHasData ? `${kpi.latestAuditPass}/${kpi.latestAuditTotal}` : noData,
            status: kpi ? kpiStatus(kpi.kpi4Compliant, auditHasData) : 'nodata',
            note: 'Target all checks',
            section: SECTION.compliance,
            testid: Testids.deliveryKpi.tileCompliance
        }
    ];
    const byId = new Map(tiles.map((t)=>[
            t.testid,
            t
        ]));
    const metrics = tiles.map((t)=>({
            id: t.testid,
            label: t.label,
            // A no-data tile states what is missing, never a bare dash.
            value: t.value,
            muted: t.status === 'nodata',
            // The status in words, so it never rests on the dot's colour alone.
            note: t.status === 'nodata' ? t.note : `${STATUS_TEXT[t.status]} · ${t.note}`,
            tone: STATUS_TONE[t.status],
            href: t.section === SECTION.financial && !financialLinkable ? undefined : `/delivery/programs/${programId}/${t.section}`
        }));
    return /*#__PURE__*/ _jsx(SpecraMetricStrip, {
        "data-testid": Testids.deliveryKpi.spine,
        getItemProps: (m)=>({
                'data-kpi-status': byId.get(m.id)?.status
            }),
        getItemTestId: (m)=>m.id,
        metrics: metrics,
        renderLink: renderRouterLink
    });
}
